/**
 * Name corrections, applied by code. Browser-safe, pure.
 *
 * The utility model proposes `heard → fixed` pairs over the space-joined
 * transcript; this module decides which to keep and applies them to the
 * frozen aligned words. A correction must look like a mis-hearing of the
 * fixed form (spelling similarity), not a different name: in the test run the
 * guard stopped "McCrack" from being "corrected" to the brand.
 */

import { distance } from "fastest-levenshtein";
import type { NameCorrection, RejectedCorrection, TimedWord } from "./types";

/** A correction's heard and fixed forms may differ by at most this share. */
export const CORRECTION_MAX_EDIT_RATIO = 0.45;
/** An inferred entity this close to the brand name is the brand. */
export const BRAND_SNAP_MAX_EDIT_RATIO = 0.3;

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Edit distance over lowercase letters/digits, as a share of the longer. */
export function nameEditRatio(a: string, b: string): number {
	const x = squash(a);
	const y = squash(b);
	if (!x.length || !y.length) return 1;
	return distance(x, y) / Math.max(x.length, y.length);
}

export function isPlausibleCorrection(heard: string, fixed: string): boolean {
	return nameEditRatio(heard, fixed) <= CORRECTION_MAX_EDIT_RATIO;
}

/** Snap a near-miss of a brand name ("Spyglass") onto the brand. */
export function snapToBrandName(
	value: string,
	brandNames: readonly string[],
): string {
	if (!value) return value;
	for (const brand of brandNames) {
		if (!brand) continue;
		if (value === brand) return brand;
		if (nameEditRatio(value, brand) <= BRAND_SNAP_MAX_EDIT_RATIO) {
			return brand;
		}
	}
	return value;
}

export interface ProposedCorrection {
	heard: string;
	fixed: string;
	reason?: string;
}

/**
 * Keep only corrections whose `heard` form appears verbatim in the text and
 * plausibly mis-hears `fixed`. `fixed` forms that nearly match the brand snap
 * to it exactly.
 */
export function filterProposedCorrections(
	proposed: readonly ProposedCorrection[],
	text: string,
	options: { brandNames?: readonly string[] } = {},
): { kept: ProposedCorrection[]; rejected: RejectedCorrection[] } {
	const kept: ProposedCorrection[] = [];
	const rejected: RejectedCorrection[] = [];
	const seen = new Set<string>();
	for (const raw of proposed) {
		const heard = raw.heard.trim();
		const fixed = snapToBrandName(
			raw.fixed.trim(),
			options.brandNames ?? [],
		);
		if (!heard || !fixed || heard === fixed) {
			rejected.push({ heard, fixed, why: "no-op" });
			continue;
		}
		if (seen.has(heard)) {
			rejected.push({ heard, fixed, why: "duplicate" });
			continue;
		}
		if (!text.includes(heard)) {
			rejected.push({
				heard,
				fixed,
				why: "not verbatim in the transcript",
			});
			continue;
		}
		if (!isPlausibleCorrection(heard, fixed)) {
			rejected.push({
				heard,
				fixed,
				why: `not a plausible mis-hearing (edit ratio ${nameEditRatio(heard, fixed).toFixed(2)})`,
			});
			continue;
		}
		seen.add(heard);
		kept.push({ heard, fixed, reason: raw.reason });
	}
	return { kept, rejected };
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const WORD_CHAR = /[A-Za-z0-9]/;

/**
 * Apply the enabled corrections to the words, longest `heard` first. A match
 * may span several words (and a piece boundary); the replacement takes the
 * time span of the words it replaces, spread by character share when the
 * fixed form has several words. Returns the corrected words and how many
 * times each correction applied (by id).
 */
export function applyCorrectionsToWords(
	words: readonly TimedWord[],
	corrections: ReadonlyArray<
		Pick<NameCorrection, "id" | "heard" | "fixed" | "enabled">
	>,
): { words: TimedWord[]; counts: Record<string, number> } {
	let ws: TimedWord[] = words.map((w) => [w[0], w[1], w[2]]);
	const counts: Record<string, number> = {};
	const active = corrections
		.filter((c) => c.enabled && c.heard.trim() && c.heard !== c.fixed)
		.sort((a, b) => b.heard.length - a.heard.length);

	for (const correction of active) {
		counts[correction.id] = 0;
		const heard = correction.heard.trim();
		const fixed = correction.fixed.trim();
		const offsets: number[] = [];
		let cursor = 0;
		for (const w of ws) {
			offsets.push(cursor);
			cursor += w[0].length + 1;
		}
		const joined = ws.map((w) => w[0]).join(" ");
		const pattern = new RegExp(
			escapeRegExp(heard).replace(/ +/g, "\\s+"),
			"g",
		);
		const matches: Array<{ a: number; b: number }> = [];
		for (const m of joined.matchAll(pattern)) {
			const a = m.index ?? 0;
			const b = a + m[0].length;
			const before = a > 0 ? joined[a - 1] : "";
			const after = b < joined.length ? joined[b] : "";
			if (
				(before && WORD_CHAR.test(before)) ||
				(after && WORD_CHAR.test(after))
			) {
				continue;
			}
			matches.push({ a, b });
		}
		if (!matches.length) continue;

		const wordAt = (charIndex: number) => {
			let lo = 0;
			let hi = offsets.length - 1;
			while (lo < hi) {
				const mid = (lo + hi + 1) >> 1;
				if ((offsets[mid] ?? 0) <= charIndex) lo = mid;
				else hi = mid - 1;
			}
			return lo;
		};

		const next = ws.slice();
		// Walk matches from the end so earlier word indices stay valid.
		for (const { a, b } of [...matches].reverse()) {
			const i = wordAt(a);
			const j = wordAt(b - 1);
			const first = ws[i];
			const last = ws[j];
			if (!first || !last) continue;
			const prefix = first[0].slice(0, a - (offsets[i] ?? 0));
			const suffix = last[0].slice(b - (offsets[j] ?? 0));
			const tokens = `${prefix}${fixed}${suffix}`
				.split(/\s+/)
				.filter(Boolean);
			const start = first[1];
			const end = Math.max(start, last[2]);
			const chars = tokens.reduce((sum, t) => sum + t.length + 1, 0);
			let pos = 0;
			const replacement: TimedWord[] = tokens.map((t) => {
				const s = start + ((end - start) * pos) / Math.max(1, chars);
				pos += t.length + 1;
				const e =
					start + ((end - start) * (pos - 1)) / Math.max(1, chars);
				return [
					t,
					Math.round(s * 1000) / 1000,
					Math.round(e * 1000) / 1000,
				];
			});
			next.splice(i, j - i + 1, ...replacement);
			counts[correction.id] = (counts[correction.id] ?? 0) + 1;
		}
		ws = next;
	}
	return { words: ws, counts };
}

/** Stable id for a correction row. */
export function correctionId(heard: string, fixed: string): string {
	let hash = 0;
	const s = `${heard}\u0000${fixed}`;
	for (let i = 0; i < s.length; i++) {
		hash = (Math.imul(31, hash) + s.charCodeAt(i)) | 0;
	}
	return `c${(hash >>> 0).toString(36)}`;
}
